import { useState } from 'react';
import { User, MapPin, Phone, Mail, Briefcase, DollarSign, Camera, CheckCircle, AlertCircle, Upload, Star } from 'lucide-react';
import { useToast } from '../components/Toast';

interface ServiceCategory {
  id: string;
  name: string;
  icon: string;
}

const categories: ServiceCategory[] = [
  { id: 'plumbing', name: 'Plomería', icon: '🚿' },
  { id: 'electrical', name: 'Electricidad', icon: '⚡' },
  { id: 'hvac', name: 'Clima/AC', icon: '❄️' },
  { id: 'cleaning', name: 'Limpieza', icon: '🧹' },
  { id: 'gardening', name: 'Jardinería', icon: '🌱' },
  { id: 'security', name: 'Seguridad', icon: '🔒' },
  { id: 'automotive', name: 'Automotriz', icon: '🚗' },
  { id: 'pets', name: 'Mascotas', icon: '🐕' },
  { id: 'tutoring', name: 'Clases Particulares', icon: '📚' },
  { id: 'health', name: 'Salud', icon: '🏥' },
  { id: 'moving', name: 'Mudanzas', icon: '📦' },
  { id: 'painting', name: 'Pintura', icon: '🎨' },
];

const serviceZones = [
  { id: 'condo', name: 'Solo mi condominio', price: 'GRATIS' },
  { id: 'neighborhood', name: 'Radio de 5km', price: '$5/mes' },
  { id: 'city', name: 'Toda la ciudad', price: '$15/mes' },
  { id: 'premium', name: 'Premium Regional', price: '$50/mes' },
];

export default function SupplierRegistration() {
  const { success, error: showError } = useToast();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    categories: [] as string[],
    serviceZone: 'neighborhood',
    description: '',
    hourlyRate: '',
    acceptsCard: true,
    acceptsCash: true,
    acceptsTransfer: true,
    hasInsurance: false,
    yearsExperience: '',
  });

  const handleCategoryToggle = (categoryId: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(c => c !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      success('¡Registro enviado! Revisaremos tu solicitud en 24-48 horas.');
      setStep(4);
    } catch (err) {
      showError('Error al enviar. Intenta de nuevo.');
    }
    setIsSubmitting(false);
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-4">Información de Contacto</h3>
      
      <div>
        <label className="block text-slate-300 text-sm mb-1">Nombre del Negocio *</label>
        <div className="relative">
          <Briefcase className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            type="text"
            value={formData.businessName}
            onChange={(e) => setFormData({...formData, businessName: e.target.value})}
            placeholder="Ej: Plomería Express"
            className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-slate-300 text-sm mb-1">Nombre del Contacto *</label>
        <div className="relative">
          <User className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            type="text"
            value={formData.contactName}
            onChange={(e) => setFormData({...formData, contactName: e.target.value})}
            placeholder="Ej: Juan Pérez"
            className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-slate-300 text-sm mb-1">Correo Electrónico *</label>
        <div className="relative">
          <Mail className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({...formData, email: e.target.value})}
            placeholder="proveedor@ejemplo.com"
            className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-slate-300 text-sm mb-1">Teléfono WhatsApp *</label>
        <div className="relative">
          <Phone className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({...formData, phone: e.target.value})}
            placeholder="+58 412 123 4567"
            className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-slate-300 text-sm mb-1">Dirección Fiscal *</label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
          <input
            type="text"
            value={formData.address}
            onChange={(e) => setFormData({...formData, address: e.target.value})}
            placeholder="Av. Principal, Edificio Torre A, Piso 3"
            className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-4">Servicios que Ofreces</h3>
      
      <p className="text-slate-400 text-sm mb-4">Selecciona todas las categorías que apliquen:</p>
      
      <div className="grid grid-cols-2 gap-3">
        {categories.map((cat) => (
          <button
            key={cat.id}
            onClick={() => handleCategoryToggle(cat.id)}
            className={`p-3 rounded-lg border text-left transition ${
              formData.categories.includes(cat.id)
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'bg-[var(--v-surface-1)] border-[var(--v-surface-3)] text-slate-300 hover:border-slate-500'
            }`}
          >
            <span className="text-2xl">{cat.icon}</span>
            <p className="text-sm mt-1">{cat.name}</p>
          </button>
        ))}
      </div>

      <div className="mt-6">
        <label className="block text-slate-300 text-sm mb-1">Descripción de Servicios *</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Describe tus servicios, experiencia, y por qué los clientes deberían elegirte..."
          rows={4}
          className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-slate-300 text-sm mb-1">Tarifa aproximada</label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={formData.hourlyRate}
              onChange={(e) => setFormData({...formData, hourlyRate: e.target.value})}
              placeholder="$20-50/hora"
              className="w-full bg-slate-700 text-white pl-10 pr-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-slate-300 text-sm mb-1">Años de experiencia</label>
          <input
            type="number"
            value={formData.yearsExperience}
            onChange={(e) => setFormData({...formData, yearsExperience: e.target.value})}
            placeholder="5"
            className="w-full bg-slate-700 text-white px-4 py-3 rounded-lg border border-slate-600 focus:border-blue-500 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white mb-4">Zona de Servicio</h3>
      
      <div className="space-y-3">
        {serviceZones.map((zone) => (
          <label
            key={zone.id}
            className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition ${
              formData.serviceZone === zone.id
                ? 'bg-blue-600/20 border-blue-500'
                : 'bg-[var(--v-surface-1)] border-[var(--v-surface-3)] hover:border-slate-500'
            }`}
          >
            <div className="flex items-center gap-3">
              <input
                type="radio"
                name="serviceZone"
                value={zone.id}
                checked={formData.serviceZone === zone.id}
                onChange={(e) => setFormData({...formData, serviceZone: e.target.value})}
                className="w-4 h-4 text-blue-600"
              />
              <div>
                <p className="text-white font-medium">{zone.name}</p>
                {zone.id === 'condo' && (
                  <p className="text-slate-400 text-xs">Visible solo para residentes de tu condominio</p>
                )}
              </div>
            </div>
            <span className={`font-bold ${zone.price === 'GRATIS' ? 'text-green-400' : 'text-blue-400'}`}>
              {zone.price}
            </span>
          </label>
        ))}
      </div>

      <div className="bg-[var(--v-surface-1)] p-4 rounded-lg border border-[var(--v-surface-3)] mt-6">
        <h4 className="font-medium text-white mb-3">Métodos de Pago Aceptados</h4>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-slate-300">
            <input
              type="checkbox"
              checked={formData.acceptsCard}
              onChange={(e) => setFormData({...formData, acceptsCard: e.target.checked})}
              className="w-4 h-4 rounded"
            />
            Tarjeta de crédito/débito
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input
              type="checkbox"
              checked={formData.acceptsCash}
              onChange={(e) => setFormData({...formData, acceptsCash: e.target.checked})}
              className="w-4 h-4 rounded"
            />
            Efectivo
          </label>
          <label className="flex items-center gap-2 text-slate-300">
            <input
              type="checkbox"
              checked={formData.acceptsTransfer}
              onChange={(e) => setFormData({...formData, acceptsTransfer: e.target.checked})}
              className="w-4 h-4 rounded"
            />
            Transferencia bancaria
          </label>
        </div>
      </div>

      <div className="bg-yellow-900/20 p-4 rounded-lg border border-yellow-500/20 mt-4">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            checked={formData.hasInsurance}
            onChange={(e) => setFormData({...formData, hasInsurance: e.target.checked})}
            className="w-4 h-4 rounded mt-1"
          />
          <div>
            <span className="text-slate-300">Cuenta con seguro de responsabilidad civil</span>
            <p className="text-slate-500 text-xs mt-1">Los proveedores con seguro obtienen una insignia especial y más confianza de los clientes.</p>
          </div>
        </label>
      </div>

      <div className="border-t border-[var(--v-surface-3)] pt-4">
        <h4 className="font-medium text-white mb-2 flex items-center gap-2">
          <Camera className="w-5 h-5 text-blue-400" />
          Fotos de Trabajos (Opcional)
        </h4>
        <div className="flex gap-2">
          <button className="flex-1 py-8 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-slate-500 hover:text-slate-300 transition">
            <Upload className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Subir foto</span>
          </button>
          <button className="flex-1 py-8 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-slate-500 hover:text-slate-300 transition">
            <Upload className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Subir foto</span>
          </button>
          <button className="flex-1 py-8 border-2 border-dashed border-slate-600 rounded-lg text-slate-400 hover:border-slate-500 hover:text-slate-300 transition">
            <Upload className="w-6 h-6 mx-auto mb-2" />
            <span className="text-sm">Subir foto</span>
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="text-center py-8">
      <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-10 h-10 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-white mb-4">¡Registro Exitoso!</h3>
      <p className="text-slate-400 mb-2">Hemos recibido tu solicitud.</p>
      <p className="text-slate-400 mb-6">
        Nuestro equipo revisará tu información en <span className="text-blue-400 font-semibold">24-48 horas</span>.
        Te notificaremos por email y WhatsApp cuando tu perfil esté activo.
      </p>
      <div className="bg-[var(--v-surface-1)] p-4 rounded-lg inline-block text-left">
        <p className="text-slate-300 text-sm">
          <span className="text-blue-400">•</span> Verificación de documentos<br/>
          <span className="text-blue-400">•</span> Validación de referencias<br/>
          <span className="text-blue-400">•</span> Activación del perfil
        </p>
      </div>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6">
          <h2 className="text-2xl font-bold text-[var(--v-text-primary)]">Registro de Proveedor</h2>
          <p className="text-blue-100 text-sm mt-1">Únete a nuestra red de profesionales de confianza</p>
        </div>

        {/* Progress */}
        {step < 4 && (
          <div className="px-6 py-4 border-b border-[var(--v-surface-3)]">
            <div className="flex items-center justify-between text-sm">
              <span className={`font-medium ${step >= 1 ? 'text-blue-400' : 'text-slate-500'}`}>Contacto</span>
              <span className={`font-medium ${step >= 2 ? 'text-blue-400' : 'text-slate-500'}`}>Servicios</span>
              <span className={`font-medium ${step >= 3 ? 'text-blue-400' : 'text-slate-500'}`}>Zona</span>
            </div>
            <div className="mt-2 w-full bg-slate-700 rounded-full h-2">
              <div 
                className="bg-blue-500 rounded-full h-2 transition-all"
                style={{ width: `${(step / 3) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </div>

        {/* Footer */}
        {step < 4 && (
          <div className="px-6 py-4 border-t border-[var(--v-surface-3)] flex justify-between">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="px-6 py-2 rounded-lg text-slate-300 hover:text-white transition"
              >
                Anterior
              </button>
            ) : (
              <div />
            )}
            
            {step < 3 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 && (!formData.businessName || !formData.contactName || !formData.email || !formData.phone)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || formData.categories.length === 0}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Completar Registro
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      {step < 4 && (
        <div className="mt-6 bg-[var(--v-surface-1)] p-4 rounded-lg border border-[var(--v-surface-3)]">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-slate-300 text-sm">
                <span className="font-semibold text-white">¿Por qué registrarte?</span>
              </p>
              <ul className="text-slate-400 text-sm mt-1 space-y-1">
                <li>• Accede a clientes verificados de tu zona</li>
                <li>• Recibe pagos seguros a través de la app</li>
                <li>• Construye tu reputación con reseñas</li>
                <li>• Gestiona tus citas y disponibilidad</li>
                <li>• Sin comisión en el plan gratuito</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
